import { formatDate, formatDateTime } from '@/lib/utils'
import { borderRadius, colors, fontSizes, fontWeights } from '@/theme'
import type { CandidatePatient, DuplicateCandidate } from '@/types/deduplication'
import { LinkOutlined, Merge } from '@mui/icons-material'
import { Box, Button, Divider, Paper, Typography } from '@mui/material'
import { Link, useLocation } from 'react-router-dom'

const LABEL_WIDTH = 120
const COL_GAP = 16

const cellSx = { px: 1.5, py: 1, borderRadius: borderRadius.sm } as const
const highlightedCellSx = { ...cellSx, bgcolor: colors.highlight.bg, border: `1px solid ${colors.highlight.border}` }
const defaultCellSx = { ...cellSx, bgcolor: colors.grey[50], border: `1px solid ${colors.border.default}` }

function getFieldValue(record: object, key: string): string {
  const val = (record as Record<string, unknown>)[key]
  if (val === null || val === undefined) return 'None'
  if (typeof val === 'boolean') return val ? 'True' : 'False'
  return String(val)
}

const MERGE_FIELDS = [
  { label: 'First Name', key: 'first_name' },
  { label: 'Last Name', key: 'last_name' },
  { label: 'Gender', key: 'gender' },
  {
    label: 'Date of birth',
    getValue: (p: CandidatePatient) => (p.date_of_birth ? formatDate(p.date_of_birth) : 'None')
  },
  { label: 'Post code', key: 'postal_code' }
]

const LINK_FIELDS = [
  ...MERGE_FIELDS,
  { label: 'User email address', getValue: (p: CandidatePatient) => p.user?.email ?? 'None' }
]

interface RecommendationCardProps {
  type: 'merge' | 'link'
  candidate: DuplicateCandidate
}

export default function RecommendationCard({ type, candidate }: RecommendationCardProps) {
  const location = useLocation()
  const patients = [candidate.patient_a, candidate.patient_b]
  const patientName = `${candidate.patient_a.first_name ?? ''} ${candidate.patient_a.last_name ?? ''}`.trim()

  const isMerge = type === 'merge'
  const fields = isMerge ? MERGE_FIELDS : LINK_FIELDS
  const Icon = isMerge ? Merge : LinkOutlined
  const title = isMerge
    ? `Merge recommendation: ${patientName} (${patients.length})`
    : `Link recommendation: ${patientName}`

  const gridCols = `${LABEL_WIDTH}px repeat(${patients.length}, 1fr)`

  function getPatientLabel(idx: number) {
    if (isMerge) return `Patient ${idx + 1}`
    return idx === 0 ? 'Patient 1 (primary patient)' : 'Patient 2'
  }

  return (
    <Paper variant='outlined' sx={{ borderRadius: borderRadius.md, overflow: 'hidden', mb: 3 }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: fontSizes.lg, fontWeight: fontWeights.semibold }}>{title}</Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 3, pt: 2.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, gap: `0 ${COL_GAP}px`, mb: 1 }}>
          <Box />
          {patients.map((patient, idx) => (
            <Box key={patient.hash}>
              <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold }}>
                {getPatientLabel(idx)}
              </Typography>
              <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
                Created on: {formatDateTime(candidate.created_at) || 'N/A'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ px: 3, pt: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, color: colors.brand.primary }}>
            Patient details
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, pb: 2.5 }}>
        {fields.map(field => {
          const values = field.getValue
            ? patients.map(p => field.getValue(p))
            : patients.map(p => getFieldValue(p, field.key))
          return (
            <Box
              key={field.label}
              sx={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                gap: `0 ${COL_GAP}px`,
                alignItems: 'center',
                py: 1
              }}
            >
              <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>{field.label}</Typography>
              {patients.map((patient, idx) => (
                <Box key={patient.hash} sx={values[idx] !== values[0] ? highlightedCellSx : defaultCellSx}>
                  <Typography sx={{ fontSize: fontSizes.base }}>{values[idx]}</Typography>
                </Box>
              ))}
            </Box>
          )
        })}
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        <Button
          component={Link}
          to={`/deduplication/${type}/${candidate.duplicate_id}`}
          state={{
            candidate,
            backLabel: 'Back to duplicates',
            fallbackPath: `/deduplication${location.search}`
          }}
          variant='primary'
          fullWidth
          startIcon={<Icon />}
          sx={{ py: 1.5 }}
        >
          Review patients
        </Button>
      </Box>
    </Paper>
  )
}
